import { useCallback } from 'react';
import { UserIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getDetectionLabel } from '@/utils/detection-classes';
import { EngineService } from '@/services/engine/engine-service';
import { revalidateEntityViews } from '@/utils/swr';
import type { EventLinkResult, MergeResult, Person } from '@/types/person';
import { MergeIdentityModal } from '../MergeIdentityModal';
import { TargetIdentityPicker } from '../TargetIdentityPicker';
import type { MergeCandidate } from '../types';
import { getErrorDetail } from '../utils';
import { MergeCandidateRow } from '../MergeCandidateRow';
import { SelectedPerson } from './SelectedPerson';

interface MergeVariantProps {
    variant: 'merge';
    sourcePerson: Person;
    onSuccess: (result: MergeResult) => void;
    onSourceStale?: () => void;
}

interface LinkVariantProps {
    variant: 'link';
    sourcePerson: Person;
    eventId: string;
    classId: number;
    onSuccess: (result: EventLinkResult) => void;
    showSuggestions?: boolean;
    titleKey?: string;
    infoKey?: string;
}

type MergePersonModalProps = {
    isOpen: boolean;
    onClose: () => void;
} & (MergeVariantProps | LinkVariantProps);

export function MergePersonModal(props: MergePersonModalProps) {
    const { isOpen, onClose } = props;
    const { t } = useTranslation([
        I18nNamespace.DIRECTORY,
        I18nNamespace.EVENTS,
    ]);

    const sourcePersonId = props.sourcePerson.id;
    const isLink = props.variant === 'link';

    const fetchSuggestions = useCallback(
        async (sourceId: string): Promise<MergeCandidate[]> => {
            const response =
                await EngineService.getPersonMergeSuggestions(sourceId);
            return response.suggestions.map((suggestion) => ({
                id: suggestion.personId,
                name: suggestion.name,
                imageUrl: suggestion.imageUrl,
                score: suggestion.similarityScore,
            }));
        },
        [],
    );

    const fetchTargets = useCallback(
        async ({
            search,
            sourceId,
            page,
            size,
        }: {
            search: string;
            sourceId: string;
            page: number;
            size: number;
        }) => {
            const response = await EngineService.searchPersonMergeTargets({
                search,
                excludePersonIds: [sourceId],
                page,
                size,
            });
            return {
                items: response.items.map((target) => ({
                    id: target.personId,
                    name: target.name,
                    imageUrl: target.imageUrl,
                    score: null,
                })),
                total: response.page.totalItems,
            };
        },
        [],
    );

    const handleConfirm = useCallback(
        async (selectedPersonId: string) => {
            try {
                if (props.variant === 'merge') {
                    const result = await EngineService.mergePersons({
                        sourcePersonId,
                        targetPersonId: selectedPersonId,
                    });
                    toast.success(t('mergeIdentityModal.successMergeSingle'));
                    // The source person is deleted by the merge; skip its cache
                    // keys so we don't refetch a now-404 person and surface a
                    // spurious "failed to load" error.
                    await revalidateEntityViews('person', (key) =>
                        key.includes(sourcePersonId),
                    );
                    props.onSuccess(result);
                } else {
                    const result = await EngineService.relinkPersonEvent(
                        props.sourcePerson.id,
                        props.eventId,
                        { targetPersonId: selectedPersonId },
                    );
                    toast.success(t('mergeIdentityModal.successLink'));
                    await revalidateEntityViews('person');
                    props.onSuccess(result);
                }
            } catch (error: unknown) {
                const detail = getErrorDetail(error);
                if (
                    props.variant === 'merge' &&
                    detail === 'SOURCE_NOT_FOUND'
                ) {
                    toast.info(t('mergeIdentityModal.errorAlreadyMerged'));
                    props.onSourceStale?.();
                    return;
                }
                if (
                    props.variant !== 'merge' &&
                    detail === 'FACE_MATCH_NOT_FOUND'
                ) {
                    toast.info(t('mergeIdentityModal.errorAlreadyLinked'));
                    return;
                }
                toast.error(
                    props.variant === 'merge'
                        ? t('mergeIdentityModal.errorMerge')
                        : t('mergeIdentityModal.errorLink'),
                );
                throw error;
            }
        },
        [props, sourcePersonId, t],
    );

    const submitText = isLink
        ? t('mergeIdentityModal.link')
        : t('mergeIdentityModal.merge');

    const linkTitleKey =
        props.variant === 'link' && props.titleKey
            ? props.titleKey
            : 'mergeIdentityModal.linkEventTitle';
    const linkInfoKey =
        props.variant === 'link' && props.infoKey
            ? props.infoKey
            : 'mergeIdentityModal.linkEventInfo';

    const titleText = isLink ? t(linkTitleKey) : t('mergeIdentityModal.title');
    const infoText = isLink ? t(linkInfoKey) : t('mergeIdentityModal.info');

    const selectedSectionLabel = isLink
        ? t('mergeIdentityModal.selectedEvent')
        : t('mergeIdentityModal.selectedIdentity');
    const targetSectionLabel = isLink
        ? t('mergeIdentityModal.linkToIdentity')
        : t('mergeIdentityModal.intoIdentity');

    const eventThumbnailUrl =
        props.variant === 'link'
            ? EngineService.getEventThumbnailUrl(props.eventId)
            : undefined;
    const eventNameOverride =
        props.variant === 'link'
            ? getDetectionLabel(t, props.classId)
            : undefined;

    const showSuggestions =
        props.variant === 'merge' ? true : (props.showSuggestions ?? false);

    return (
        <MergeIdentityModal
            isOpen={isOpen}
            onClose={onClose}
            titleText={titleText}
            infoText={infoText}
            submitText={submitText}
            isLink={isLink}
            onConfirm={handleConfirm}
            renderSource={() => (
                <SelectedPerson
                    person={props.sourcePerson}
                    sectionLabel={selectedSectionLabel}
                    imageUrlOverride={eventThumbnailUrl}
                    nameOverride={eventNameOverride}
                />
            )}
            renderTargetPicker={({ selectedId, onSelect, onClear }) => (
                <TargetIdentityPicker
                    sourceId={sourcePersonId}
                    selectedId={selectedId}
                    onSelect={(id) => onSelect(id)}
                    onClear={onClear}
                    showSuggestions={showSuggestions}
                    sectionLabel={targetSectionLabel}
                    swrKey="person-merge-target-picker"
                    fallbackIcon={
                        <UserIcon className="text-grey-400" size={28} />
                    }
                    labels={{
                        searchPlaceholder: t(
                            'mergeIdentityModal.searchPlaceholder',
                        ),
                        empty: t('mergeIdentityModal.noPersonsFound'),
                        select: t('mergeIdentityModal.selectPerson'),
                        unknown: t('mergeIdentityModal.unknownPerson'),
                        clear: t('mergeIdentityModal.clearSelection'),
                    }}
                    fetchSuggestions={fetchSuggestions}
                    fetchTargets={fetchTargets}
                    renderRow={(item, { isSelected, label }) => (
                        <MergeCandidateRow
                            item={item}
                            label={label}
                            isSelected={isSelected}
                            fallbackIcon={
                                <UserIcon className="text-grey-300" size={28} />
                            }
                        />
                    )}
                />
            )}
        />
    );
}
